import React from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { CustomSelect } from "ui/Input/Select"
import EquipmentFilters from "../EquipmentFilters"
import { CALENDAR_TABS, DEFAULT_START_HOUR, DEFAULT_END_HOUR } from "../../constants"
import TimeRangeSettings from "../TimeRangeSettings"
import styles from "../../Calendar.module.css"

const DesktopFiltersRow = ({ activeTab, params, filters, crewFilters, setFilters, setCrewFilters, handleFilterChange, experienceOptions, crewMemberOptionsWithUnassigned, equipmentOptions, roles, calendarFilterSelectProps, setEquipmentCategoryOptions, canCreateBooking, setIsScheduleTimeOffModalOpen, handleNewBooking, active, isWideDesktop, showTimeRangeSettings, startHour, endHour, onStartHourChange, onEndHourChange, onResetHours }) => {
  const { t } = useTranslation()

  const renderFilters = () => {
    if (activeTab === CALENDAR_TABS.CREW) {
      return (
        <>
          <div className={styles.filterSelectWrapper}>
            <CustomSelect mode="multiple" placeholder="Roles" style={{ width: "100%" }} className={styles.calendarFilterSelect} {...calendarFilterSelectProps} value={crewFilters.roles} onChange={(v) => setCrewFilters((prev) => ({ ...prev, roles: v }))} options={Array.isArray(roles) ? roles.map((r) => ({ label: r.name, value: r.id })) : []} maxTagCount={0} allowClear showSearch={false} tagRender={() => null} />
            {crewFilters.roles.length > 0 && <div className={styles.filterCountOverlay}>{`Roles (${crewFilters.roles.length})`}</div>}
          </div>
          <div className={styles.filterSelectWrapper}>
            <CustomSelect mode="multiple" placeholder="Crew Member" style={{ width: "100%" }} className={styles.calendarFilterSelect} {...calendarFilterSelectProps} value={crewFilters.crewMembers} onChange={(v) => setCrewFilters((prev) => ({ ...prev, crewMembers: v }))} options={crewMemberOptionsWithUnassigned} maxTagCount={0} allowClear showSearch={false} tagRender={() => null} />
            {crewFilters.crewMembers.length > 0 && <div className={styles.filterCountOverlay}>{`Crew Member (${crewFilters.crewMembers.length})`}</div>}
          </div>
        </>
      )
    }
    if (activeTab === CALENDAR_TABS.EQUIPMENT) {
      return <EquipmentFilters value={filters.equipmentFilters || {}} onChange={(val) => setFilters((prev) => ({ ...prev, equipmentFilters: val }))} onOptionsChange={({ categoryOptions }) => setEquipmentCategoryOptions(categoryOptions || [])} hideSearch selectProps={calendarFilterSelectProps} />
    }
    return (
      <>
        <div className={styles.filterSelectWrapper}>
          <CustomSelect mode="multiple" placeholder={t("calendar.filters.experience", "Experience")} className={styles.calendarFilterSelect} style={{ width: "100%" }} {...calendarFilterSelectProps} value={filters.experience} onChange={handleFilterChange("experience")} options={experienceOptions} maxTagCount={0} allowClear showSearch={false} tagRender={() => null} />
          {filters.experience.length > 0 && <div className={styles.filterCountOverlay}>{`${t("calendar.filters.experience", "Experience")} (${filters.experience.length})`}</div>}
        </div>
        {activeTab !== CALENDAR_TABS.MY_BOOKINGS && (
          <div className={styles.filterSelectWrapper}>
            <CustomSelect mode="multiple" style={{ width: "100%" }} placeholder={t("calendar.filters.crew_member", "Crew Member")} className={styles.calendarFilterSelect} {...calendarFilterSelectProps} value={filters.crew} onChange={handleFilterChange("crew")} options={crewMemberOptionsWithUnassigned} maxTagCount={0} allowClear showSearch={false} tagRender={() => null} />
            {filters.crew.length > 0 && <div className={styles.filterCountOverlay}>{`${t("calendar.filters.crew_member", "Crew Member")} (${filters.crew.length})`}</div>}
          </div>
        )}
        <div className={styles.filterSelectWrapper}>
          <CustomSelect mode="multiple" placeholder={t("calendar.filters.equipment", "Equipment")} className={styles.calendarFilterSelect} style={{ width: "100%" }} {...calendarFilterSelectProps} value={filters.equipment} onChange={handleFilterChange("equipment")} options={equipmentOptions} maxTagCount={0} allowClear showSearch={false} tagRender={() => null} />
          {filters.equipment.length > 0 && <div className={styles.filterCountOverlay}>{`${t("calendar.filters.equipment", "Equipment")} (${filters.equipment.length})`}</div>}
        </div>
      </>
    )
  }

  return (
    <Flex justify="space-between" className={`px-s py-xs ${styles.calendarFiltersRow}`} gap={8} wrap>
      <Flex gap={12} align="center" className={styles.calendarFilters}>{renderFilters()}</Flex>
      {isWideDesktop && (
        <Flex gap={10} className={`${styles.calendarActions} ml-auto`}>
          {(activeTab === CALENDAR_TABS.MY_BOOKINGS || activeTab === CALENDAR_TABS.CREW) && (
            <Button color="primary" variant="outlined" className={styles.scheduleTimeOff} size="middle" onClick={active ? () => setIsScheduleTimeOffModalOpen(true) : undefined} disabled={!active}>
              {t("unavailable_days.schedule_time_off")}
            </Button>
          )}
          {canCreateBooking && (
            <Button type="primary" className={styles.newBooking} size="middle" onClick={active ? handleNewBooking : undefined} disabled={!active}>
              {t("bookings.add")}
            </Button>
          )}
          {showTimeRangeSettings && (
            <TimeRangeSettings startHour={startHour} endHour={endHour} onStartChange={onStartHourChange} onEndChange={onEndHourChange} onReset={onResetHours} isModified={startHour !== DEFAULT_START_HOUR || endHour !== DEFAULT_END_HOUR} />
          )}
        </Flex>
      )}
    </Flex>
  )
}

DesktopFiltersRow.propTypes = {
  activeTab: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  crewFilters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  setCrewFilters: PropTypes.func.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  experienceOptions: PropTypes.array.isRequired,
  crewMemberOptionsWithUnassigned: PropTypes.array.isRequired,
  equipmentOptions: PropTypes.array.isRequired,
  roles: PropTypes.array,
  calendarFilterSelectProps: PropTypes.object,
  setEquipmentCategoryOptions: PropTypes.func.isRequired,
  canCreateBooking: PropTypes.bool,
  setIsScheduleTimeOffModalOpen: PropTypes.func.isRequired,
  handleNewBooking: PropTypes.func.isRequired,
  active: PropTypes.bool,
  isWideDesktop: PropTypes.bool,
  showTimeRangeSettings: PropTypes.bool,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
  onStartHourChange: PropTypes.func,
  onEndHourChange: PropTypes.func,
  onResetHours: PropTypes.func,
}

export default DesktopFiltersRow
